import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRightIcon,
  CheckCircle2Icon,
  KeyRoundIcon,
  LockIcon,
  ShieldAlertIcon,
} from "lucide-react";
import { supabase } from "../lib/supabaseClient";

const isMissingSessionError = (error) => {
  if (!error) return false;

  return (
    error.name === "AuthSessionMissingError" ||
    error.code === "session_not_found" ||
    error.message?.toLowerCase().includes("auth session missing")
  );
};

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [isRecoveryReady, setIsRecoveryReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const checkSession = async () => {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (!isMounted) return;

      if (error && !isMissingSessionError(error)) {
        console.error(
          "Kunde inte läsa återställningssession:",
          error
        );
      }

      setIsRecoveryReady(Boolean(session?.user));
      setIsCheckingSession(false);
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;

      if (event === "PASSWORD_RECOVERY" || session?.user) {
        setIsRecoveryReady(true);
        setIsCheckingSession(false);
      }

      if (event === "SIGNED_OUT") {
        setIsRecoveryReady(false);
      }
    });

    checkSession();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleUpdatePassword = async (event) => {
    event.preventDefault();

    if (!password || !confirmPassword) {
      setErrorMessage("Fyll i båda lösenordsfälten.");
      return;
    }

    if (password.length < 6) {
      setErrorMessage(
        "Lösenordet måste innehålla minst 6 tecken."
      );
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Lösenorden matchar inte.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const { error: updateError } =
        await supabase.auth.updateUser({
          password,
        });

      if (updateError) {
        console.error("Kunde inte uppdatera lösenord:", updateError);
        setErrorMessage(
          updateError.message ||
            "Lösenordet kunde inte uppdateras. Begär en ny återställningslänk."
        );
        return;
      }

      const { error: signOutError } =
        await supabase.auth.signOut({
          scope: "local",
        });

      if (
        signOutError &&
        !isMissingSessionError(signOutError)
      ) {
        console.warn(
          "Lösenordet ändrades, men sessionen kunde inte avslutas:",
          signOutError
        );
      }

      setIsComplete(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f0e7] text-[#1f2d26]">
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        <div className="absolute -right-32 -top-28 h-96 w-96 rounded-full bg-[#dfe9e1] blur-3xl" />
        <div className="absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-[#eee2c8] blur-3xl" />
      </div>

      <main className="relative z-10 mx-auto flex min-h-screen w-full max-w-5xl items-center px-4 py-8 sm:px-6 sm:py-12">
        <div className="mx-auto w-full max-w-md">
          <section className="overflow-hidden rounded-[28px] border border-[#d8d2c4] bg-[#fffdf8] shadow-[0_22px_65px_rgba(31,45,38,0.12)]">
            <div className="relative overflow-hidden bg-[#183d2d] px-6 py-7 text-white sm:px-8 sm:py-8">
              <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-[#b08b45]/20 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-28 -left-16 h-60 w-60 rounded-full bg-white/10 blur-3xl" />

              <div className="relative">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/15 bg-white/10 shadow-sm">
                    <img
                      src="https://yswafknmqpwjrsxjslbu.supabase.co/storage/v1/object/public/logos//logosmall.png"
                      alt="HoleInOne"
                      className="h-8 w-8 object-contain brightness-125"
                    />
                  </div>

                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/55">
                      HoleInOne.se
                    </p>
                    <p className="mt-0.5 text-sm font-medium text-white/80">
                      Tournament manager
                    </p>
                  </div>
                </div>

                <p className="mt-7 text-[10px] font-semibold uppercase tracking-[0.26em] text-[#d7c28e]">
                  Kontoåterställning
                </p>

                <h1 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">
                  Välj nytt lösenord
                </h1>

                <p className="mt-2 max-w-sm text-sm leading-6 text-white/60">
                  Skriv in det lösenord du vill använda framöver.
                </p>
              </div>
            </div>

            {isCheckingSession ? (
              <div className="px-6 py-10 text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#e5ede7] text-[#183d2d]">
                  <KeyRoundIcon className="h-5 w-5" />
                </span>

                <p className="mt-4 text-sm font-semibold text-[#183d2d]">
                  Kontrollerar återställningslänken…
                </p>
              </div>
            ) : isComplete ? (
              <div className="px-6 py-8 text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#e5ede7] text-[#287148]">
                  <CheckCircle2Icon className="h-6 w-6" />
                </span>

                <h2 className="mt-4 text-lg font-semibold text-[#183d2d]">
                  Lösenordet är uppdaterat
                </h2>

                <p className="mt-2 text-sm leading-6 text-[#68716c]">
                  Återställningssessionen är avslutad. Logga in med ditt nya
                  lösenord.
                </p>

                <button
                  type="button"
                  onClick={() => window.location.replace("/login")}
                  className="group mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#214d39]"
                >
                  Logga in med nytt lösenord
                  <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </button>
              </div>
            ) : !isRecoveryReady ? (
              <div className="px-6 py-8 text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#f3e8e5] text-[#a0443e]">
                  <ShieldAlertIcon className="h-6 w-6" />
                </span>

                <h2 className="mt-4 text-lg font-semibold text-[#183d2d]">
                  Länken kunde inte användas
                </h2>

                <p className="mt-2 text-sm leading-6 text-[#68716c]">
                  Återställningslänken kan vara ogiltig eller ha gått ut.
                  Begär en ny länk från inloggningssidan.
                </p>

                <Link
                  to="/login"
                  className="mt-5 inline-flex w-full items-center justify-center rounded-xl bg-[#183d2d] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#214d39]"
                >
                  Till inloggningen
                </Link>
              </div>
            ) : (
              <form
                onSubmit={handleUpdatePassword}
                className="space-y-4 px-5 py-6 sm:px-7 sm:py-7"
              >
                <div>
                  <label
                    htmlFor="new-password"
                    className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.16em] text-[#68716c]"
                  >
                    Nytt lösenord
                  </label>

                  <div className="relative">
                    <LockIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8d948f]" />

                    <input
                      id="new-password"
                      type="password"
                      autoComplete="new-password"
                      placeholder="Minst 6 tecken"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      className="w-full rounded-xl border border-[#b8b09f] bg-[#fffdf8] py-3 pl-10 pr-3 text-sm text-[#1f2d26] outline-none placeholder:text-[#a0a59f] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/12"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="confirm-password"
                    className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.16em] text-[#68716c]"
                  >
                    Upprepa lösenord
                  </label>

                  <div className="relative">
                    <LockIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8d948f]" />

                    <input
                      id="confirm-password"
                      type="password"
                      autoComplete="new-password"
                      placeholder="Upprepa lösenordet"
                      value={confirmPassword}
                      onChange={(event) =>
                        setConfirmPassword(event.target.value)
                      }
                      className="w-full rounded-xl border border-[#b8b09f] bg-[#fffdf8] py-3 pl-10 pr-3 text-sm text-[#1f2d26] outline-none placeholder:text-[#a0a59f] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/12"
                    />
                  </div>
                </div>

                {errorMessage && (
                  <div
                    className="flex items-start gap-3 rounded-xl border border-[#dfc5c0] bg-[#fff8f6] px-3.5 py-3 text-sm text-[#a0443e]"
                    role="alert"
                  >
                    <ShieldAlertIcon className="mt-0.5 h-4 w-4 shrink-0" />
                    <p className="leading-5">{errorMessage}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-5 py-3 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(24,61,45,0.17)] transition hover:bg-[#214d39] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <KeyRoundIcon className="h-4 w-4" />
                  {isSubmitting
                    ? "Sparar lösenord…"
                    : "Spara nytt lösenord"}
                </button>
              </form>
            )}

            <footer className="border-t border-[#e4ded1] bg-[#f7f4ec] px-5 py-4 text-center text-[10px] text-[#7b817d]">
              © {new Date().getFullYear()} HoleInOne.se · En tjänst från
              Hollander Web
            </footer>
          </section>
        </div>
      </main>
    </div>
  );
}